// Libraries
import { Injectable } from '@angular/core';
import { Angular2TokenService } from 'angular2-token';
import {
  Request,
  XHRBackend,
  RequestOptions,
  Response,
  Headers,
  Http,
  RequestOptionsArgs
} from '@angular/http';
import {
  Router
} from '@angular/router';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/throw';
import {
  ToastsManager,
  Toast
} from 'ng2-toastr/ng2-toastr';

/**
 * `HttpService` intercept backend requests to manage global errors.
 */
@Injectable()
export class HttpService extends Http {
  /**
   * Check if server is online or not.
   */
  private serverOnline = true;
  /**
   * Check if current session is expired or not.
   */
  private sessionExpired = false;
  /**
   * Check if a server error occurred
   */
  private serverError = false;

  /**
   * Creates a new `HttpService` instance.
   */
  constructor(
    protected backend: XHRBackend,
    protected defaultOptions: RequestOptions,
    protected toastr: ToastsManager,
    protected router: Router,
  ) {
    super(backend, defaultOptions);

    // Fix angular 2 token library
    this.fixAngular2Token();
  }

  /**
   * Make a request.
   * @param url Url path
   * @param options Request options
   */
  request(url: string | Request, options?: RequestOptionsArgs): Observable<Response> {
    return super.request(url, options).catch((res: Response) => {
      // Check if server is online or not
      if (res.status === 0) {
        // Show message only once
        if (this.serverOnline) {
          this.toastr.error(
            'El servidor está caído, comuniquese con el administrador.',
            'Error!',
            { dismiss: 'controlled' }
          ).then((toast: Toast) => {
              setTimeout(() => {
                this.toastr.dismissToast(toast);
                this.serverOnline = true;
              }, 5000);
          });
        }
        // Redirect to login
        this.router.navigate(['/login']);
        // Toggle flag
        this.serverOnline = false;
      } else {
        this.serverOnline = true;
        // Check if session is expired or not
        if (res.status === 401 && res.url.indexOf('sign_in') < 0) {
          // Show message only once
          if (!this.sessionExpired) {
            this.toastr.warning(
              'La sesión ha expirado.',
              'Advertencia!',
              { dismiss: 'controlled' }
            ).then((toast: Toast) => {
                setTimeout(() => {
                  this.toastr.dismissToast(toast);
                  this.sessionExpired = false;
                }, 5000);
            });
            // Redirect to login
            this.router.navigate(['/login']);
            // Toggle flag
            this.sessionExpired = true;
          }
        } else {
          // Normal request
          this.sessionExpired = false;
          // Show backend error message
          if (res.status >= 500 && res.status <= 512) {
            if (!this.serverError) {
              // Show message
              this.toastr.error(
                'Se ha producido un error al establecer la comunicación con los servidores. Inténtelo de nuevo más tarde.',
                'Error!',
                { dismiss: 'controlled' }
              ).then((toast: Toast) => {
                  setTimeout(() => {
                    this.toastr.dismissToast(toast);
                    this.serverError = false;
                  }, 5000);
              });
              // Toggle flag
              this.serverError = true;
            }
          } else if (res.status === 422) {
            // Show message
            this.toastr.warning(
              'No es posible realizar esta acción, revisa los datos ingresados.',
              'Advertencia!'
            );
          } else if (res.status === 403) {
            // Show message
            this.toastr.warning(
              'No posee los permisos suficientes para realizar esta operación.',
              'Advertencia!'
            );
          }
        }
      }

      return Observable.throw(res);
    });
  }

  /**
   * Override request method for Angular2TokenService. Get auth data from storage before make request
   * https://github.com/neroniaky/angular2-token/issues/260
   */
  private fixAngular2Token() {
    Angular2TokenService.prototype.request = function (options) {
      let baseRequestOptions;
      let baseHeaders = this.atOptions.globalOptions.headers;
      // Get auth data from local storage
      this.getAuthDataFromStorage();
      // Merge auth headers to request if set
      if (this.atCurrentAuthData != null) {
        Object.assign(baseHeaders, {
            'access-token': this.atCurrentAuthData.accessToken,
            'client': this.atCurrentAuthData.client,
            'expiry': this.atCurrentAuthData.expiry,
            'token-type': this.atCurrentAuthData.tokenType,
            'uid': this.atCurrentAuthData.uid
        });
      }
      baseRequestOptions = new RequestOptions({
          headers: new Headers(baseHeaders)
      });
      // Merge standard and custom RequestOptions
      baseRequestOptions = baseRequestOptions.merge(options);
      let response = this.http.request(new Request(baseRequestOptions)).share();
      this.handleResponse(response);
      return response;
    };
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/angular-router-loader/src!./~/tslint-loader!./src/app/core/http.service.ts